import React from 'react';
import {
  Dialog,
  DialogProps,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export interface ResponsiveModalProps {
  open: boolean;
  onClose: () => void;
  /** Modal title (header). */
  title?: React.ReactNode;
  /** Optional secondary line under the title. */
  subtitle?: React.ReactNode;
  /** Optional leading icon shown next to the title. */
  icon?: React.ReactNode;
  /** Body content. */
  children?: React.ReactNode;
  /** Footer actions (buttons). Rendered in a sticky footer. */
  actions?: React.ReactNode;
  /** Desktop max width. Default 'sm'. */
  maxWidth?: DialogProps['maxWidth'];
  /** Show a full-cover loading spinner over the body. */
  loading?: boolean;
  /** Error message shown as an Alert at the top of the body. */
  error?: string | null;
  /** Hide the header close (X) button. */
  hideCloseButton?: boolean;
  /** Prevent closing on backdrop click / Escape (e.g. while submitting). */
  disableClose?: boolean;
  /**
   * Below this breakpoint the dialog becomes a full-screen / bottom-sheet style
   * surface. Default 'sm'.
   */
  mobileBreakpoint?: 'sm' | 'md';
  /** Render mobile variant as a bottom sheet (rounded top) instead of full screen. */
  bottomSheet?: boolean;
  /** Extra sx applied to the Dialog paper. */
  paperSx?: DialogProps['PaperProps'] extends { sx?: infer S } ? S : any;
  /** Optional id for aria-labelledby wiring. */
  id?: string;
}

/**
 * Mobile-first responsive dialog primitive.
 *
 * - Full-screen (or bottom-sheet) below `mobileBreakpoint`, centered card above it.
 * - Clean header / scrollable body / sticky footer structure.
 * - Built-in loading overlay + error alert + accessible close button.
 * - Fully theme-token driven, works in light & dark, RTL-aware.
 *
 * Business logic stays in the caller — this only owns layout & a11y.
 */
const ResponsiveModal: React.FC<ResponsiveModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  children,
  actions,
  maxWidth = 'sm',
  loading = false,
  error,
  hideCloseButton = false,
  disableClose = false,
  mobileBreakpoint = 'sm',
  bottomSheet = false,
  paperSx,
  id,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));
  const titleId = id ? `${id}-title` : undefined;

  const handleClose = (_e: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (disableClose || loading) return;
    if (reason === 'backdropClick' && bottomSheet) {
      // bottom sheets still allow backdrop close
    }
    onClose();
  };

  const fullScreen = isMobile && !bottomSheet;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth={maxWidth}
      aria-labelledby={titleId}
      dir={theme.direction}
      PaperProps={{
        dir: theme.direction,
        sx: {
          // Centered card has comfortable rounding; mobile bottom-sheet rounds only the top.
          borderRadius: fullScreen ? 0 : bottomSheet && isMobile ? '16px 16px 0 0' : 2,
          ...(bottomSheet && isMobile
            ? {
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                m: 0,
                width: '100%',
                maxWidth: '100%',
                maxHeight: '92vh',
              }
            : {
                maxHeight: fullScreen ? '100%' : 'calc(100% - 64px)',
              }),
          ...paperSx,
        },
      }}
      sx={{
        ...(bottomSheet && isMobile
          ? { '& .MuiDialog-container': { alignItems: 'flex-end' } }
          : {}),
      }}
    >
      {/* Header */}
      {(title || !hideCloseButton) && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: { xs: 2, sm: 3 },
              py: 2,
              flexShrink: 0,
            }}
          >
            {icon && (
              <Box sx={{ display: 'flex', color: 'primary.main', flexShrink: 0 }}>{icon}</Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {title && (
                <Typography
                  id={titleId}
                  variant="h6"
                  sx={{ fontWeight: 600, lineHeight: 1.3, wordBreak: 'break-word' }}
                >
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
            {!hideCloseButton && (
              <IconButton
                onClick={onClose}
                disabled={disableClose || loading}
                aria-label="بستن"
                size="small"
                sx={{ flexShrink: 0, color: 'text.secondary' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Divider />
        </>
      )}

      {/* Body (scrollable) */}
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: { xs: 2, sm: 3 },
          py: 2.5,
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {children}

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (t) =>
                t.palette.mode === 'dark' ? 'rgba(15,23,42,0.55)' : 'rgba(255,255,255,0.6)',
              backdropFilter: 'blur(1px)',
              zIndex: 2,
            }}
          >
            <CircularProgress size={36} />
          </Box>
        )}
      </Box>

      {/* Footer (sticky) */}
      {actions && (
        <>
          <Divider />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              flexWrap: 'wrap',
              px: { xs: 2, sm: 3 },
              py: 1.5,
              flexShrink: 0,
              // Comfortable tap targets stacked full-width on mobile.
              '& > .MuiButton-root': {
                minHeight: 42,
                flex: { xs: '1 1 auto', sm: '0 0 auto' },
              },
            }}
          >
            {actions}
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default ResponsiveModal;
